import {
  collection,
  addDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  setDoc,
  where,
  updateDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export type FeedPostType =
  | 'post'
  | 'lead_created'
  | 'opportunity_created'
  | 'task_created'
  | 'lead_stage_changed'
  | 'opportunity_stage_changed'
  | 'echobot_tip'
  | 'echobot_announcement'
  | 'echobot_help'
  | 'echobot_milestone';

export interface FeedPost {
  id: string;
  orgId: string;
  type: FeedPostType;
  content?: string;
  entityType?: 'lead' | 'opportunity' | 'task' | 'customer';
  entityId?: string;
  entityLabel?: string;
  createdAt: Date;
  createdById: string;
  createdByName: string;
  commentsCount?: number;
  attachments?: {
    url: string;
    name: string;
    type: string;
    size: number;
  }[];
  // Echobot-specific fields
  echobotAnimation?: 'celebrate' | 'question' | 'shake' | 'wave';
  echobotActionButton?: {
    label: string;
    action: string;
  };
}

export interface FeedComment {
  id: string;
  postId: string;
  orgId: string;
  content: string;
  createdAt: Date;
  createdById: string;
  createdByName: string;
}

export const postsService = {
  onFeed(orgId: string, callback: (posts: FeedPost[]) => void, onError?: (e: any) => void): () => void {
    console.log('Setting up posts subscription for orgId:', orgId);
    
    if (!orgId) {
      console.error('No orgId provided for posts subscription');
      onError?.(new Error('No orgId provided'));
      return () => {};
    }
    
    const q = query(
      collection(db, 'posts'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, 
      (snap) => {
        try {
          console.log('Received posts snapshot:', snap.docs.length, 'posts');
          const items = snap.docs
            .map((d) => {
              const data = d.data();
              return { 
                id: d.id, 
                ...data,
                createdAt: data.createdAt?.toDate?.() || new Date(),
                attachments: data.attachments || []
              };
            }) as FeedPost[];
          console.log('Processed posts:', items);
          callback(items);
        } catch (e) {
          console.error('Error processing posts:', e);
          onError?.(e);
        }
      }, 
      (e) => {
        console.error('Posts subscription error:', e);
        // If it's a missing index error, provide helpful message
        if (e.code === 'failed-precondition' && e.message?.includes('index')) {
          console.error('MISSING INDEX: Please create a composite index for (orgId, createdAt desc) in Firebase Console');
          console.error('Or click this link from the error message in Firebase Console to auto-create the index');
        }
        onError?.(e);
      }
    );
  },

  async uploadFile(file: File, postId: string): Promise<{ url: string; name: string; type: string; size: number }> {
    try {
      const fileName = `posts/${postId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      
      return {
        url,
        name: file.name,
        type: file.type,
        size: file.size
      };
    } catch (error: any) {
      console.error('File upload failed:', error);
      // If storage is not configured, return a placeholder
      if (error.code === 'storage/unauthorized' || error.code === 'storage/unknown') {
        console.warn('Firebase Storage not configured. Files cannot be uploaded.');
        throw new Error('Firebase Storage is not configured. Please enable it in Firebase Console.');
      }
      throw error;
    }
  },

  async createUserPost(params: {
    orgId: string;
    content: string;
    createdById: string;
    createdByName: string;
    entityType?: 'lead' | 'opportunity' | 'task' | 'customer';
    entityId?: string;
    entityLabel?: string;
    files?: File[];
  }): Promise<string> {
    try {
      console.log('Creating post with params:', {
        orgId: params.orgId,
        content: params.content,
        createdById: params.createdById,
        createdByName: params.createdByName,
        entityType: params.entityType
      });
      
      // Create post first
      const postData = {
        orgId: params.orgId,
        type: 'post',
        content: params.content,
        entityType: params.entityType || null,
        entityId: params.entityId || null,
        entityLabel: params.entityLabel || null,
        createdById: params.createdById,
        createdByName: params.createdByName,
        createdAt: serverTimestamp(),
        commentsCount: 0,
        attachments: []
      };
      
      const ref = await addDoc(collection(db, 'posts'), postData);
      console.log('Post created successfully with ID:', ref.id);
      
      // Upload files if any
      if (params.files && params.files.length > 0) {
        console.log('Uploading', params.files.length, 'files...');
        const attachments = await Promise.all(
          params.files.map(file => this.uploadFile(file, ref.id))
        );
        
        // Update post with attachments
        await updateDoc(doc(db, 'posts', ref.id), {
          attachments
        });
        console.log('Files uploaded successfully');
      }
      
      return ref.id;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  },

  async createSystemPost(params: {
    orgId: string;
    type: Extract<FeedPostType, 'lead_created' | 'opportunity_created' | 'task_created' | 'lead_stage_changed' | 'opportunity_stage_changed'>;
    createdById: string;
    createdByName: string;
    entityType: 'lead' | 'opportunity' | 'task';
    entityId: string;
    entityLabel: string;
  }): Promise<void> {
    await addDoc(collection(db, 'posts'), {
      orgId: params.orgId,
      type: params.type,
      content: null,
      entityType: params.entityType,
      entityId: params.entityId,
      entityLabel: params.entityLabel,
      createdById: params.createdById,
      createdByName: params.createdByName,
      createdAt: serverTimestamp(),
      commentsCount: 0,
    } as any);
  },

  async createEchobotPost(params: {
    orgId: string;
    type: Extract<FeedPostType, 'echobot_tip' | 'echobot_announcement' | 'echobot_help' | 'echobot_milestone'>;
    content: string;
    animation?: 'celebrate' | 'question' | 'shake' | 'wave';
    actionButton?: {
      label: string;
      action: string;
    };
  }): Promise<string> {
    const ref = await addDoc(collection(db, 'posts'), {
      orgId: params.orgId,
      type: params.type,
      content: params.content,
      createdById: 'echobot',
      createdByName: 'Echobot 🤖',
      createdAt: serverTimestamp(),
      commentsCount: 0,
      echobotAnimation: params.animation || 'wave',
      echobotActionButton: params.actionButton || null,
    } as any);
    
    console.log(`✅ Echobot post created: ${params.type}`);
    return ref.id;
  },

  async addComment(postId: string, params: { orgId: string; content: string; createdById: string; createdByName: string }): Promise<string> {
    const ref = await addDoc(collection(db, 'posts', postId, 'comments'), {
      orgId: params.orgId,
      content: params.content,
      createdById: params.createdById,
      createdByName: params.createdByName,
      createdAt: serverTimestamp(),
    } as any);
    return ref.id;
  },

  onComments(postId: string, callback: (comments: FeedComment[]) => void, onError?: (e: any) => void): () => void {
    const q = query(collection(db, 'posts', postId, 'comments'), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => {
      try {
        const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any), createdAt: (d.data() as any).createdAt?.toDate?.() || new Date() })) as FeedComment[];
        callback(items);
      } catch (e) {
        onError?.(e);
      }
    }, (e) => onError?.(e));
  }
};


